import React, { useState } from 'react';
import styled from 'styled-components';
import { Formik } from 'formik';
import InputField from '../../app/common/components/InputField';
import ErrorMessage from '../../app/common/components/errorMessage/ErrorMessage';
import Button from '../../app/common/components/Button';
import ButtonOutline from '../../app/common/components/ButtonOutline';
import { COLORS, FONT_FAMILY, MQS } from '../../app/common/constants/styles';
import { TranslationHelpers } from '../../i18n/translationHelpers';
import { IErrorMessage } from '../../app/common/components/errorMessage/errorMessage.type';
import { FormattedMessage } from 'react-intl';
import { RenderChallenge } from '../../utils/challengerMain';

const Text = styled.div`
    margin: 30px 0px 50px 0px;
    line-height: 1.5;

    & p {
        margin: 0;
    }
`;

const Actions = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 20px;

    & > button:first-child {
        border-color: ${COLORS.rakutenRed};
        color: ${COLORS.rakutenRed};
        margin: 0;
    }

    & > button {
        max-width: 45%;
        width: 200px;
    }
`;

const ChallengerWrapper = styled.div`
    margin-top: 50px;
    margin-bottom: 60px;
    input.c-input {
        background-color: transparent;
        border: none;
        border-bottom: 1px solid;
        border-bottom-color: ${COLORS.grey3};
        border-radius: 0;
        color: ${COLORS.dark};
        display: block;
        ${FONT_FAMILY.multipleRegularCss};
        font-weight: 300;
        padding: 1px 1px 5px 1px;
        width: 100%;

        :placeholder-shown {
            padding-left: 0px;

            @media (min-width: ${MQS.mq1Min}) {
                font-size: 0.875rem;
            }

            @media (min-width: ${MQS.mq2Max}) {
                font-size: 1rem;
            }
        }

        @media (max-width: ${MQS.mq2Min}) {
            padding-left: 0;
        }

        :focus {
            outline: none;
            border-bottom: 2px solid ${COLORS.rakutenRed};
        }

        [disabled] {
            opacity: 1; // required for iOS safari;
            -webkit-text-fill-color: ${COLORS.grey3};
        }
    }
`;

export interface IDeleteConfirmationProps {
    onAccept: Function;
    onClose?: Function;
    apiError?: number;
    deleting: boolean;
    formattedMessage: React.ReactElement;
    confirmText: FormattedMessage.MessageDescriptor;
    challengerPid: string;
    challengerReadyCallback?: (ready: boolean) => void;
}

interface IErrors {
    password?: IErrorMessage;
}

const DeleteConfirmation = ({
    onAccept,
    onClose,
    apiError,
    deleting,
    formattedMessage,
    confirmText,
    challengerPid,
    challengerReadyCallback,
}: IDeleteConfirmationProps) => {
    const [isChallengeLoaded, setIsChallengeLoaded] = useState(!challengerPid);

    const onChallengeReady = (ready: boolean) => {
        if (challengerReadyCallback) {
            challengerReadyCallback(ready);
        }
        setIsChallengeLoaded(ready);
    };

    function renderChallenge(challengerPid: string) {
        return (
            <ChallengerWrapper>
                <RenderChallenge cpid={challengerPid} isReadyCallback={onChallengeReady} />
            </ChallengerWrapper>
        );
    }

    return (
        <div data-qa-id="drs-delete-confirmation">
            <Text data-qa-id="delete-confirmation-formatted-message">{formattedMessage}</Text>
            <Formik
                initialValues={{
                    password: '',
                }}
                validate={(values) => {
                    const errors: IErrors = {};

                    if (!values.password) {
                        errors.password =
                            TranslationHelpers.getMessageDescriptor('error.password.missing');
                    }

                    return errors;
                }}
                onSubmit={(values, { setSubmitting }) => {
                    setSubmitting(true);
                    onAccept(values.password);
                }}
            >
                {({ errors, values, touched, handleSubmit, handleReset }) => (
                    <form onSubmit={handleSubmit}>
                        <InputField
                            type="password"
                            id="password"
                            name="password"
                            value={values.password}
                            error={values.password && errors.password}
                            required={true}
                            labelMessage={TranslationHelpers.getMessageDescriptor('label.password')}
                        >
                            {touched.password && errors.password && (
                                <ErrorMessage message={errors.password} />
                            )}
                            {apiError && apiError === 401 && values.password && (
                                <ErrorMessage
                                    message={TranslationHelpers.getMessageDescriptor(
                                        'error.password.incorrect',
                                    )}
                                />
                            )}
                            {apiError && apiError === 403 && values.password && (
                                <ErrorMessage
                                    message={TranslationHelpers.getMessageDescriptor(
                                        'error.session_expired',
                                    )}
                                />
                            )}
                        </InputField>
                        {challengerPid && renderChallenge(challengerPid)}
                        <Actions>
                            <ButtonOutline
                                data-qa-id="delete-confirmation-confirm"
                                id="confirm"
                                type="submit"
                                onClick={handleSubmit}
                                loading={deleting}
                                disabled={deleting}
                                text={confirmText}
                            />
                            {onClose && (
                                <Button
                                    qaId="delete-confirmation-cancel"
                                    id="cancel"
                                    primary
                                    type="button"
                                    text={TranslationHelpers.getMessageDescriptor('button.cancel')}
                                    disabled={deleting}
                                    onClick={() => {
                                        handleReset();
                                        onClose();
                                    }}
                                    success={false}
                                />
                            )}
                        </Actions>
                    </form>
                )}
            </Formik>
        </div>
    );
};

export default DeleteConfirmation;
